import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

const LEADERBOARD_QUERY_KEY = ['maintenance_snake_scores', 'leaderboard'] as const

const GRID_W = 22
const GRID_H = 16
const CELL = 18
const TICK_MS = 118

type Cell = { x: number; y: number }

type BoardRow = {
  username: string
  score: number
  created_at: string
}

function wrap(v: number, max: number): number {
  return ((v % max) + max) % max
}

function randomFood(occupied: Set<string>): Cell {
  for (let n = 0; n < 800; n++) {
    const x = Math.floor(Math.random() * GRID_W)
    const y = Math.floor(Math.random() * GRID_H)
    const k = `${x},${y}`
    if (!occupied.has(k)) return { x, y }
  }
  return { x: 0, y: 0 }
}

const keyToDir = (key: string): Cell | null => {
  const k = key.toLowerCase()
  if (k === 'arrowup' || k === 'w') return { x: 0, y: -1 }
  if (k === 'arrowdown' || k === 's') return { x: 0, y: 1 }
  if (k === 'arrowleft' || k === 'a') return { x: -1, y: 0 }
  if (k === 'arrowright' || k === 'd') return { x: 1, y: 0 }
  return null
}

function opposite(a: Cell, b: Cell): boolean {
  return a.x === -b.x && a.y === -b.y
}

async function fetchLeaderboard(): Promise<BoardRow[]> {
  const { data, error } = await supabase
    .from('maintenance_snake_scores')
    .select('username, score, created_at')
    .order('score', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(20)

  if (error) throw error
  return (data ?? []) as BoardRow[]
}

export function MaintenanceSnakeGame() {
  const queryClient = useQueryClient()
  const profile = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const canPostScore = isAuthenticated && !!profile?.username

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const snakeRef = useRef<Cell[]>([])
  const dirRef = useRef<Cell>({ x: 1, y: 0 })
  const pendingDirRef = useRef<Cell>({ x: 1, y: 0 })
  const foodRef = useRef<Cell>({ x: 10, y: 8 })
  const scoreRef = useRef(0)
  const aliveRef = useRef(false)
  const tickRef = useRef<number>()

  const [score, setScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const leaderboardQuery = useQuery({
    queryKey: LEADERBOARD_QUERY_KEY,
    queryFn: fetchLeaderboard,
    staleTime: 30 * 1000,
  })

  const submitMutation = useMutation({
    mutationFn: async (value: number) => {
      const { data, error } = await supabase.rpc('submit_maintenance_snake_score', { p_score: value })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: LEADERBOARD_QUERY_KEY })
    },
  })

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const root = document.documentElement
    const primary = getComputedStyle(root).getPropertyValue('--primary').trim() || '239 84% 67%'
    const accent = getComputedStyle(root).getPropertyValue('--accent').trim() || '330 81% 60%'
    const primaryHsl = `hsl(${primary})`
    const accentHsl = `hsl(${accent})`

    const w = GRID_W * CELL
    const h = GRID_H * CELL
    ctx.clearRect(0, 0, w, h)

    ctx.fillStyle = 'rgba(0,0,0,0.45)'
    ctx.fillRect(0, 0, w, h)

    ctx.strokeStyle = 'rgba(255,255,255,0.06)'
    ctx.lineWidth = 1
    for (let x = 0; x <= GRID_W; x++) {
      ctx.beginPath()
      ctx.moveTo(x * CELL, 0)
      ctx.lineTo(x * CELL, h)
      ctx.stroke()
    }
    for (let y = 0; y <= GRID_H; y++) {
      ctx.beginPath()
      ctx.moveTo(0, y * CELL)
      ctx.lineTo(w, y * CELL)
      ctx.stroke()
    }

    const food = foodRef.current
    ctx.fillStyle = accentHsl
    ctx.shadowColor = accentHsl
    ctx.globalAlpha = 0.95
    ctx.shadowBlur = 14
    ctx.fillRect(food.x * CELL + 2, food.y * CELL + 2, CELL - 4, CELL - 4)
    ctx.shadowBlur = 0
    ctx.globalAlpha = 1

    const snake = snakeRef.current
    snake.forEach((seg, i) => {
      const t = i === 0 ? 1 : i / Math.max(snake.length - 1, 1)
      ctx.fillStyle =
        i === 0 ? primaryHsl : `hsl(${239 + t * 20} 84% ${58 - t * 12}% / ${0.5 + (1 - t) * 0.4})`
      const pad = i === 0 ? 1 : 2
      ctx.fillRect(seg.x * CELL + pad, seg.y * CELL + pad, CELL - pad * 2, CELL - pad * 2)
    })
  }, [])

  const resetGame = useCallback(() => {
    const startX = Math.floor(GRID_W / 2)
    const startY = Math.floor(GRID_H / 2)
    snakeRef.current = [
      { x: startX, y: startY },
      { x: startX - 1, y: startY },
      { x: startX - 2, y: startY },
    ]
    dirRef.current = { x: 1, y: 0 }
    pendingDirRef.current = { x: 1, y: 0 }
    const occ = new Set(snakeRef.current.map((c) => `${c.x},${c.y}`))
    foodRef.current = randomFood(occ)
    scoreRef.current = 0
    setScore(0)
    aliveRef.current = true
    setGameOver(false)
    setSaveError(null)
    setPlaying(true)
    requestAnimationFrame(draw)
  }, [draw])

  const endGame = useCallback(() => {
    aliveRef.current = false
    setPlaying(false)
    setGameOver(true)
    if (tickRef.current) window.clearInterval(tickRef.current)
  }, [])

  const tick = useCallback(() => {
    if (!aliveRef.current) return

    const nextDir = pendingDirRef.current
    if (!opposite(nextDir, dirRef.current)) {
      dirRef.current = nextDir
    }
    const d = dirRef.current
    const snake = snakeRef.current
    const head = snake[0]
    const nh = {
      x: wrap(head.x + d.x, GRID_W),
      y: wrap(head.y + d.y, GRID_H),
    }

    const food = foodRef.current
    const ate = nh.x === food.x && nh.y === food.y

    const hitsBody = ate
      ? snake.some((s) => s.x === nh.x && s.y === nh.y)
      : snake.slice(0, -1).some((s) => s.x === nh.x && s.y === nh.y)

    if (hitsBody) {
      endGame()
      return
    }

    const newSnake = [nh, ...snake]
    if (!ate) newSnake.pop()
    else {
      scoreRef.current += 1
      setScore(scoreRef.current)
      const occ = new Set(newSnake.map((c) => `${c.x},${c.y}`))
      foodRef.current = randomFood(occ)
    }
    snakeRef.current = newSnake
    draw()
  }, [draw, endGame])

  useEffect(() => {
    if (!playing) return
    tickRef.current = window.setInterval(tick, TICK_MS)
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current)
    }
  }, [playing, tick])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const d = keyToDir(e.key)
      if (!d) return
      e.preventDefault()
      if (!aliveRef.current && !gameOver) {
        resetGame()
        pendingDirRef.current = d
        return
      }
      if (!aliveRef.current) return
      if (!opposite(d, dirRef.current)) pendingDirRef.current = d
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [gameOver, resetGame])

  const queueDir = (d: Cell) => {
    if (!aliveRef.current && !gameOver) {
      resetGame()
      pendingDirRef.current = d
      return
    }
    if (!aliveRef.current) return
    if (!opposite(d, dirRef.current)) pendingDirRef.current = d
  }

  const postScore = async () => {
    setSaveError(null)
    try {
      await submitMutation.mutateAsync(scoreRef.current)
      setGameOver(false)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not save score.'
      setSaveError(msg)
    }
  }

  const canvasW = GRID_W * CELL
  const canvasH = GRID_H * CELL
  const rows = leaderboardQuery.data ?? []

  return (
    <section
      className="mt-12 w-full max-w-3xl rounded-2xl border border-white/10 bg-black/30 p-4 shadow-2xl backdrop-blur-md sm:p-6"
      aria-label="Snake mini-game and leaderboard"
    >
      <div className="mb-4 text-left">
        <h2 className="text-lg font-semibold text-white">Snake while you wait</h2>
        <p className="mt-1 text-sm text-white/60">
          Arrow keys or WASD. Edges wrap (infinite board). Running into yourself ends the run. D-pad on smaller
          screens.
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col items-center">
          <div className="relative rounded-xl border border-white/10 bg-black/40 p-2 shadow-inner">
            <canvas
              ref={canvasRef}
              width={canvasW}
              height={canvasH}
              className="mx-auto max-h-[min(50vh,360px)] w-auto rounded-lg touch-none"
              style={{ imageRendering: 'pixelated' }}
              aria-label="Snake game board"
            />
            {!playing && !gameOver && (
              <button
                type="button"
                className="absolute inset-0 m-2 flex cursor-pointer flex-col items-center justify-center rounded-lg bg-black/55 text-center text-sm text-white/90 backdrop-blur-[2px] transition hover:bg-black/65"
                onClick={() => resetGame()}
              >
                <span className="text-base font-medium">Click or press a direction to start</span>
                <span className="mt-2 text-xs text-white/60">Eat the pink squares. Wrap through the edges.</span>
              </button>
            )}
            {gameOver && (
              <div className="absolute inset-0 m-2 flex flex-col items-center justify-center rounded-lg bg-black/70 p-4 text-center backdrop-blur-sm">
                <p className="text-lg font-semibold text-white">Run ended</p>
                <p className="mt-1 text-sm text-white/70">Score: {score}</p>
                {canPostScore ? (
                  <div className="mt-4 w-full max-w-xs space-y-2">
                    <p className="text-xs text-white/60">
                      Post to the global leaderboard as{' '}
                      <span className="font-mono text-white/90">@{profile?.username}</span>
                    </p>
                    {saveError ? <p className="text-xs text-red-400">{saveError}</p> : null}
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={submitMutation.isPending}
                        onClick={() => void postScore()}
                        className="bg-primary text-primary-foreground"
                      >
                        {submitMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                            Saving…
                          </>
                        ) : (
                          'Post score'
                        )}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSaveError(null)
                          setGameOver(false)
                          resetGame()
                        }}
                      >
                        Play again
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 max-w-xs space-y-3 text-sm text-white/75">
                    <p>Sign in to post your score with your profile username.</p>
                    <Button type="button" size="sm" variant="secondary" asChild>
                      <Link to="/auth">Go to sign in</Link>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-white/80"
                      onClick={() => {
                        setGameOver(false)
                        resetGame()
                      }}
                    >
                      Play again
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="mt-3 font-mono text-sm text-white/80">Score: {score}</p>

          <div className="mt-4 grid grid-cols-3 gap-1.5 lg:hidden" aria-label="Direction pad">
            <span />
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="h-10 w-10 bg-white/10"
              onClick={() => queueDir({ x: 0, y: -1 })}
              aria-label="Up"
            >
              ↑
            </Button>
            <span />
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="h-10 w-10 bg-white/10"
              onClick={() => queueDir({ x: -1, y: 0 })}
              aria-label="Left"
            >
              ←
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="h-10 w-10 bg-white/10"
              onClick={() => queueDir({ x: 0, y: 1 })}
              aria-label="Down"
            >
              ↓
            </Button>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="h-10 w-10 bg-white/10"
              onClick={() => queueDir({ x: 1, y: 0 })}
              aria-label="Right"
            >
              →
            </Button>
          </div>
        </div>

        <div className="w-full shrink-0 lg:w-56">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/80">Leaderboard</h3>
          {leaderboardQuery.isLoading ? (
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/25 p-4 text-sm text-white/60">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
              Loading…
            </div>
          ) : leaderboardQuery.isError ? (
            <p className="rounded-xl border border-white/10 bg-black/25 p-3 text-sm text-red-400">
              Could not load scores. If this is a fresh deploy, apply migration{' '}
              <code className="rounded bg-white/10 px-1 py-0.5 text-xs">093_maintenance_snake_leaderboard.sql</code>.
            </p>
          ) : (
            <ol className="max-h-[min(52vh,420px)] space-y-2 overflow-y-auto rounded-xl border border-white/10 bg-black/25 p-3 text-sm scrollbar-thin">
              {rows.length === 0 ? (
                <li className="text-white/45">No scores yet. Be the first.</li>
              ) : (
                rows.map((e, i) => (
                  <li key={`${e.created_at}-${e.username}-${i}`} className="flex items-center gap-2 text-white/85">
                    <span className="w-6 shrink-0 text-right text-xs text-white/40">{i + 1}.</span>
                    <span className="min-w-0 flex-1 truncate font-medium" title={e.username}>
                      @{e.username}
                    </span>
                    <span className="shrink-0 font-mono text-primary">{e.score}</span>
                  </li>
                ))
              )}
            </ol>
          )}
        </div>
      </div>
    </section>
  )
}
